import React, { useState } from "react";
import {
  Search,
  X,
  FileText,
  FileCode,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  AlertCircle,
  Clock,
  CheckCircle2,
  PlayCircle,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useListCourtScraperDocumentsQuery,
  useGetCourtFilterOptionsQuery,
  type DocumentRow,
} from "@/redux/api/courtScraper.rtkq";
import { PdfViewerModal, ExtractedTextModal } from "./DocumentModals";

export const DocumentsTable: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [courtName, setCourtName] = useState<string>("");
  const [decisionType, setDecisionType] = useState<string>("");
  const [nlpStatus, setNlpStatus] = useState<string>("");
  const [downloadStatus, setDownloadStatus] = useState<string>("");

  // Modals state
  const [selectedDocForPdf, setSelectedDocForPdf] = useState<DocumentRow | null>(null);
  const [selectedDocForText, setSelectedDocForText] = useState<DocumentRow | null>(null);

  // Filters data
  const { data: filterOptions } = useGetCourtFilterOptionsQuery();

  // Query documents
  const { data, isLoading, isFetching } = useListCourtScraperDocumentsQuery({
    page,
    pageSize,
    search: search || undefined,
    courtName: courtName || undefined,
    decisionType: decisionType || undefined,
    nlpStatus: nlpStatus || undefined,
    downloadStatus: downloadStatus || undefined,
  });

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearch("");
    setSearchInput("");
    setCourtName("");
    setDecisionType("");
    setNlpStatus("");
    setDownloadStatus("");
    setPage(1);
  };

  const hasActiveFilters = Boolean(
    search || courtName || decisionType || nlpStatus || downloadStatus
  );

  const renderNlpBadge = (doc: DocumentRow) => {
    if (doc.consolidationStatus !== "consolidated") {
      return (
        <Badge variant="outline" className="text-slate-400 border-slate-200 text-[11px]">
          טרם אוחד
        </Badge>
      );
    }
    if (!doc.nlpStatus) {
      return (
        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border-amber-200 text-[11px] gap-1">
          <Clock className="w-3 h-3" />
          ממתין לתור
        </Badge>
      );
    }
    switch (doc.nlpStatus) {
      case "completed":
        return (
          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200 text-[11px] gap-1">
            <CheckCircle2 className="w-3 h-3" />
            הושלם
          </Badge>
        );
      case "running":
        return (
          <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 border-blue-200 text-[11px] gap-1">
            <PlayCircle className="w-3 h-3" />
            בעיבוד
          </Badge>
        );
      case "failed":
        return (
          <Badge
            className="bg-rose-100 text-rose-800 hover:bg-rose-100 border-rose-200 text-[11px] gap-1"
            title={doc.nlpError || "שגיאת עיבוד"}
          >
            <AlertCircle className="w-3 h-3" />
            נכשל
          </Badge>
        );
      case "noise":
      case "skipped":
        return (
          <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 border-slate-200 text-[11px]">
            סונן / רעש
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="text-[11px]">
            {doc.nlpStatus}
          </Badge>
        );
    }
  };

  const renderDownloadBadge = (status: string) => {
    switch (status) {
      case "downloaded":
        return (
          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[11px]">
            הורד
          </Badge>
        );
      case "pending":
        return (
          <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[11px]">
            ממתין
          </Badge>
        );
      case "failed":
        return (
          <Badge className="bg-rose-50 text-rose-700 border-rose-200 text-[11px]">
            נכשל
          </Badge>
        );
      case "unavailable":
        return (
          <Badge className="bg-slate-50 text-slate-500 border-slate-200 text-[11px]">
            לא קיים באתר
          </Badge>
        );
      default:
        return <Badge variant="outline" className="text-[11px]">{status}</Badge>;
    }
  };

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 1;

  return (
    <Card className="border-slate-200 bg-white shadow-sm overflow-hidden">
      <CardContent className="p-0">
        {/* Filter Bar */}
        <div className="p-4 border-b border-slate-100 bg-slate-50/50 space-y-3">
          <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
            {/* Search Input */}
            <form
              onSubmit={handleSearchSubmit}
              className="flex items-center gap-2 w-full md:max-w-md"
            >
              <div className="relative w-full">
                <Search className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input
                  type="text"
                  placeholder="חיפוש לפי מספר תיק (14322-03-26), שם תיק או קובץ..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pr-9 pl-3 text-xs bg-white"
                />
              </div>
              <Button
                type="submit"
                size="sm"
                className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
              >
                חפש
              </Button>
            </form>

            {/* Total Results Counter */}
            <div className="text-xs text-slate-500 flex items-center gap-2 shrink-0">
              {isFetching && (
                <div className="w-3.5 h-3.5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              )}
              <span>נמצאו {total.toLocaleString()} מסמכים</span>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearFilters}
                  className="text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 h-7 px-2 flex items-center gap-1"
                >
                  <X className="w-3 h-3" />
                  נקה סינונים
                </Button>
              )}
            </div>
          </div>

          {/* Dropdown Filters */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
            {/* Court Filter */}
            <Select
              value={courtName}
              onValueChange={(val) => {
                setCourtName(val === "ALL" ? "" : val);
                setPage(1);
              }}
            >
              <SelectTrigger className="text-xs bg-white h-8">
                <SelectValue placeholder="כל בתי המשפט" />
              </SelectTrigger>
              <SelectContent dir="rtl" className="max-h-60 text-xs">
                <SelectItem value="ALL">כל בתי המשפט</SelectItem>
                {(filterOptions?.courts || []).map((c, i) => (
                  <SelectItem key={i} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Decision Type Filter */}
            <Select
              value={decisionType}
              onValueChange={(val) => {
                setDecisionType(val === "ALL" ? "" : val);
                setPage(1);
              }}
            >
              <SelectTrigger className="text-xs bg-white h-8">
                <SelectValue placeholder="כל סוגי ההחלטות" />
              </SelectTrigger>
              <SelectContent dir="rtl" className="max-h-60 text-xs">
                <SelectItem value="ALL">כל סוגי ההחלטות</SelectItem>
                {(filterOptions?.decisionTypes || []).map((d, i) => (
                  <SelectItem key={i} value={d}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* NLP Status Filter */}
            <Select
              value={nlpStatus}
              onValueChange={(val) => {
                setNlpStatus(val === "ALL" ? "" : val);
                setPage(1);
              }}
            >
              <SelectTrigger className="text-xs bg-white h-8">
                <SelectValue placeholder="סטטוס NLP" />
              </SelectTrigger>
              <SelectContent dir="rtl" className="text-xs">
                <SelectItem value="ALL">כל סטטוסי NLP</SelectItem>
                <SelectItem value="completed">הושלם בהצלחה</SelectItem>
                <SelectItem value="running">בריצה כעת</SelectItem>
                <SelectItem value="pending">בתור NLP</SelectItem>
                <SelectItem value="awaiting">ממתין לתור NLP</SelectItem>
                <SelectItem value="failed">נכשל</SelectItem>
                <SelectItem value="noise">רעש / סונן</SelectItem>
              </SelectContent>
            </Select>

            {/* Download Status Filter */}
            <Select
              value={downloadStatus}
              onValueChange={(val) => {
                setDownloadStatus(val === "ALL" ? "" : val);
                setPage(1);
              }}
            >
              <SelectTrigger className="text-xs bg-white h-8">
                <SelectValue placeholder="סטטוס הורדת PDF" />
              </SelectTrigger>
              <SelectContent dir="rtl" className="text-xs">
                <SelectItem value="ALL">כל סטטוסי ההורדה</SelectItem>
                <SelectItem value="downloaded">הורד בהצלחה</SelectItem>
                <SelectItem value="pending">ממתין להורדה</SelectItem>
                <SelectItem value="failed">נכשל בהורדה</SelectItem>
                <SelectItem value="unavailable">לא קיים באתר נט המשפט</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Documents Data Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-right text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-semibold">
              <tr>
                <th className="py-3 px-3">תיק</th>
                <th className="py-3 px-3">בית משפט ושופט</th>
                <th className="py-3 px-3">סוג החלטה ותאריך</th>
                <th className="py-3 px-3">עמודים וגודל</th>
                <th className="py-3 px-3">הורדה</th>
                <th className="py-3 px-3">סטטוס NLP</th>
                <th className="py-3 px-3 text-center">פעולות צפייה</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-400">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                      <span>טוען רשימת מסמכים...</span>
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-400">
                    לא נמצאו מסמכים התואמים את החיפוש והסינונים שנבחרו.
                  </td>
                </tr>
              ) : (
                items.map((doc) => (
                  <tr
                    key={doc.id}
                    className="hover:bg-indigo-50/40 transition-colors"
                  >
                    {/* Case info */}
                    <td className="py-3 px-3 max-w-[220px]">
                      <div className="font-semibold text-slate-900 truncate">
                        {doc.caseDisplayIdentifier || doc.filename}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate" title={doc.caseName || ""}>
                        {doc.caseName || "ללא שם תיק"}
                      </div>
                    </td>

                    {/* Court & Judge */}
                    <td className="py-3 px-3 max-w-[200px]">
                      <div className="text-slate-800 truncate font-medium">
                        {doc.courtName || "לא צוין"}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate" title={doc.judgeName || ""}>
                        {doc.judgeName || "לא צוין שופט"}
                      </div>
                    </td>

                    {/* Decision info */}
                    <td className="py-3 px-3">
                      <div className="text-slate-800 font-medium truncate max-w-[150px]">
                        {doc.decisionTypeName || "החלטה"}
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono">
                        {doc.decisionSignatureDate || "-"}
                      </div>
                    </td>

                    {/* Pages & Size */}
                    <td className="py-3 px-3 text-slate-600 font-mono text-[11px]">
                      <div>{doc.splitPageCount ? `${doc.splitPageCount} עמ'` : "-"}</div>
                      <div className="text-slate-400">
                        {doc.fileSizeKB ? `${doc.fileSizeKB} KB` : "-"}
                      </div>
                    </td>

                    {/* Download status */}
                    <td className="py-3 px-3">
                      {renderDownloadBadge(doc.downloadStatus)}
                    </td>

                    {/* NLP status */}
                    <td className="py-3 px-3">
                      {renderNlpBadge(doc)}
                    </td>

                    {/* Actions */}
                    <td className="py-3 px-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-[11px] px-2 flex items-center gap-1 border-slate-200 hover:border-indigo-300 hover:text-indigo-600"
                          onClick={() => setSelectedDocForPdf(doc)}
                          disabled={doc.downloadStatus !== "downloaded"}
                          title={doc.downloadStatus !== "downloaded" ? "הקובץ טרם הורד" : "צפה בקובץ PDF"}
                        >
                          <FileText className="w-3.5 h-3.5 text-rose-500" />
                          <span>PDF</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-[11px] px-2 flex items-center gap-1 border-slate-200 hover:border-indigo-300 hover:text-indigo-600"
                          onClick={() => setSelectedDocForText(doc)}
                          disabled={doc.consolidationStatus !== "consolidated"}
                          title={doc.consolidationStatus !== "consolidated" ? "טרם חולץ טקסט" : "צפה בטקסט שחולץ"}
                        >
                          <FileCode className="w-3.5 h-3.5 text-indigo-600" />
                          <span>טקסט</span>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-600">
          <div className="flex items-center gap-2">
            <span>הצג:</span>
            <Select
              value={String(pageSize)}
              onValueChange={(val) => {
                setPageSize(Number(val));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-18 h-7 text-xs bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent dir="rtl" className="text-xs">
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span>מסמכים בעמוד</span>
          </div>

          <div className="flex items-center gap-2">
            <span>
              עמוד {page} מתוך {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="w-7 h-7"
                onClick={() => setPage(1)}
                disabled={page <= 1 || isLoading}
              >
                <ChevronsRight className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="w-7 h-7"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || isLoading}
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="w-7 h-7"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || isLoading}
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="w-7 h-7"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages || isLoading}
              >
                <ChevronsLeft className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>

      {/* PDF Viewer Dialog */}
      <PdfViewerModal
        document={selectedDocForPdf}
        isOpen={Boolean(selectedDocForPdf)}
        onClose={() => setSelectedDocForPdf(null)}
      />

      {/* Extracted Text Dialog */}
      <ExtractedTextModal
        document={selectedDocForText}
        isOpen={Boolean(selectedDocForText)}
        onClose={() => setSelectedDocForText(null)}
      />
    </Card>
  );
};
